// Internal reusable helpers extracted from bulky TimeSeriesGraph component
import * as d3 from "d3";
import moment from "moment";
import { formatValue, progressiveSample } from "./chartHelpers";

// Build y domain given series visibility and fallback rules
export function buildYDomain({
  name,
  data1,
  data2,
  showElectrical,
  showThermal,
}) {
  // name retained for signature compatibility
  const hasDual = data1 && data1.length && data2 && data2.length;
  if (hasDual) {
    const vals = [];
    if (showElectrical) vals.push(...data1);
    if (showThermal) vals.push(...data2);
    if (!vals.length) vals.push(...data1, ...data2); // ensure domain when both toggled off momentarily
    return d3.extent(vals);
  }
  // Single-series fallback: prefer data1 if present else data2
  return d3.extent(data1 && data1.length ? data1 : data2);
}

export function makeScales(labels, yDomain, innerWidth, height) {
  const x = d3.scaleTime().domain(d3.extent(labels)).range([0, innerWidth]);
  const y = d3.scaleLinear().domain(yDomain).nice().range([height, 0]);
  return { x, y };
}

export function createAxes(g, { x, y, height, dark, axisTickFontSize, xTickFormatter, dailyMode }) {
  const xAxis = dailyMode
    ? d3.axisBottom(x).ticks(d3.timeHour.every(1))
    : d3.axisBottom(x).ticks(10);
  if (typeof xTickFormatter === "function") {
    xAxis.tickFormat(xTickFormatter);
  }
  
  // For small ranges, show decimals; for large ranges, show integers
  const yDomain = y.domain();
  const yRange = Math.abs(yDomain[1] - yDomain[0]);
  const shouldShowDecimals = yRange < 5; // Small ranges need decimals
  
  const yAxis = d3
    .axisLeft(y)
    .ticks(4)
    .tickFormat((d) => {
      if (shouldShowDecimals) {
        // For small ranges, show 1 decimal place without rounding
        return d.toFixed(1);
      }
      return formatValue(d);
    });
  const xAxisG = g
    .append("g")
    .attr("transform", `translate(0,${height})`)
    .call(xAxis)
    .call((sel) =>
      sel
        .selectAll("text")
        .style("fill", dark ? "#eee" : "#222")
        .style("font-size", axisTickFontSize + "px")
        .style("font-family", "Nunito, sans-serif")
    );
  const yAxisG = g
    .append("g")
    .call(yAxis)
    .call((sel) =>
      sel
        .selectAll("text")
        .style("fill", dark ? "#eee" : "#222")
        .style("font-size", axisTickFontSize + "px")
        .style("font-family", "Nunito, sans-serif")
    );
  try {
    yAxisG.select(".domain").remove();
  } catch (_) {}
  return { xAxis, yAxis, xAxisG, yAxisG };
}

export function buildLegend(rootEl, dark) {
  return d3
    .select(rootEl)
    .append("div")
    .attr("class", "chart-legend")
    .style("position", "absolute")
    .style("top", "4px")
    .style("left", "43%")
    .style("transform", "translateX(-50%)")
    .style("padding", "4px 10px")
    .style("font-size", "12px")
    .style("font-family", "Nunito, sans-serif")
    .style("display", "flex")
    .style("align-items", "center")
    .style("gap", "10px")
    .style("border", "1px solid " + (dark ? "#444" : "#ccc"))
    .style("background", dark ? "#1a1a1a" : "#ffffff")
    .style("color", dark ? "#eee" : "#222")
    .style("border-radius", "4px")
    .style(
      "box-shadow",
      dark ? "0 1px 3px rgba(0,0,0,0.6)" : "0 1px 3px rgba(0,0,0,0.2)"
    )
    .style("z-index", 25)
    .style("pointer-events", "auto")
    .style("user-select", "none");
}

// legendHTML now supports an optional externally provided interval (minutes) override via intervalMinutesProp.
// If provided and valid (>0), it replaces the internally derived median interval when computing energy.
export function legendHTML({
  dual,
  name,
  showElectrical,
  showThermal,
  onlyThermal,
  data1,
  data2,
  labels,
  currentScale,
  dark,
  formatValue,
  intervalMinutesProp,
  ashrae,
  yAxisName,
  legendLabels
}) {
  // Provide simple i18n-able labels via legendLabels; fall back to English literals
  const L = {
    electricalLoad: "Electrical Load",
    thermalLoad: "Thermal Load",
    min: "Min",
    max: "Max",
    avg: "Avg",
    energy: "Energy",
    ...legendLabels,
  };
  const bisectAll = d3.bisector((d) => d);
  const domain = currentScale.domain();
  const startIndex = Math.min(
    labels.length - 1,
    Math.max(0, bisectAll.left(labels, domain[0]))
  );
  const endIndex = Math.min(
    labels.length - 1,
    Math.max(startIndex, bisectAll.right(labels, domain[1]) - 1)
  );
  const count = endIndex - startIndex + 1;
  // Derive interval duration (minutes) using median of diffs inside the current window for robustness
  let intervalMinutes = 0;
  if (count > 1) {
    const diffs = [];
    for (let i = startIndex; i < endIndex; i++) {
      const d = (labels[i + 1] - labels[i]) / 60000; // minutes
      if (d > 0 && isFinite(d)) diffs.push(d);
    }
    if (diffs.length) {
      diffs.sort((a, b) => a - b);
      intervalMinutes = diffs[Math.floor(diffs.length / 2)]; // median
    }
  }
  // Fallback: if interval couldn't be determined assume 15 minutes (default stated)
  if (intervalMinutesProp != null) {
    const parsed = parseFloat(intervalMinutesProp);
    if (parsed > 0 && isFinite(parsed)) {
      intervalMinutes = parsed; // external override wins if valid
    } else if (!intervalMinutes || !isFinite(intervalMinutes)) {
      // fallback if both computed and provided invalid
      intervalMinutes = 15;
    }
  } else if (!intervalMinutes || !isFinite(intervalMinutes)) {
    intervalMinutes = 15; // internal fallback
  }
  const intervalHours = intervalMinutes / 60;
  const primaryColor = onlyThermal ? "#3dcd58" : "#42b4e6";
  const secondaryColor = "#3dcd58";
  const box = (color) =>
    `<span style="display:inline-block;width:12px;height:12px;background:${color};border:1px solid ${
      dark ? "#555" : "#ccc"
    };border-radius:2px;margin-right:4px"></span>`;
  if (dual) {
    // Compute stats for both series inside current zoom range
    const computeStats = (arr) => {
      let localMin = Infinity,
        localMax = -Infinity,
        sum = 0,
        n = 0;
      for (let i = startIndex; i <= endIndex; i++) {
        const v = arr[i];
        if (typeof v === "number" && !isNaN(v)) {
          if (v < localMin) localMin = v;
          if (v > localMax) localMax = v;
          sum += v;
          n++;
        }
      }
      if (n === 0) return { localMin: 0, localMax: 0, mean: 0, energy: 0 };
      const mean = sum / n; // average power over points
      const energy = sum * intervalHours; // kWh = sum(kW)*hoursPerPoint
      return { localMin, localMax, mean, energy };
    };
    const statsElec = computeStats(data1);
    const statsTherm = computeStats(data2);
    const part = (stats) => {
      const base = `${L.min}:${formatValue(stats.localMin) +  " " + yAxisName}, ${L.max}:${formatValue(
        stats.localMax
      ) + " " + yAxisName}, ${L.avg}:${formatValue(stats.mean) + " " + yAxisName}`;
      if (!L.energy) return base;
      return `${base}, ${L.energy}:${formatValue(stats.energy)} ${ashrae ? "Wh" : yAxisName ? yAxisName+"h" :"kWh"}`;
    };
    return (
      `<div style="display:flex;align-items:center;gap:18px;">` +
      `<span data-series="electrical" style="display:inline-flex;align-items:center;gap:6px;opacity:${
        showElectrical ? 1 : 0.35
      };cursor:pointer;">${box(
        primaryColor
      )}<span style="white-space:nowrap;font-weight:600;">${L.electricalLoad}</span><span style="font-size:11px;opacity:0.85;white-space:nowrap;">${part(
        statsElec
      )}</span></span>` +
      `<span style="width:1px;height:16px;background:${
        dark ? "#444" : "#ccc"
      };opacity:0.7;"></span>` +
      `<span data-series="thermal" style="display:inline-flex;align-items:center;gap:6px;opacity:${
        showThermal ? 1 : 0.35
      };cursor:pointer;">${box(
        secondaryColor
      )}<span style="white-space:nowrap;font-weight:600;">${L.thermalLoad}</span><span style="font-size:11px;opacity:0.85;white-space:nowrap;">${part(
        statsTherm
      )}</span></span>` +
      `</div>`
    );
  }
  const seriesData = data1.length ? data1 : data2;
  let localMin = Infinity,
    localMax = -Infinity,
    sum = 0,
    n = 0;
  for (let i = startIndex; i <= endIndex; i++) {
    const v = seriesData[i];
    if (typeof v === "number" && !isNaN(v)) {
      if (v < localMin) localMin = v;
      if (v > localMax) localMax = v;
      sum += v;
      n++;
    }
  }
  if (localMin === Infinity) {
    localMin = 0;
    localMax = 0;
    sum = 0;
  }
  const mean = n ? sum / n : 0;
  const energy = sum * intervalHours; // kWh from kW values
  const label = onlyThermal ? L.thermalLoad : L.electricalLoad || `${name} Load`;
  const baseStats = `${label} (${L.min}: ${formatValue(
    localMin
  ) + " " + yAxisName} , ${L.max}: ${formatValue(localMax) + " " + yAxisName}, ${L.avg}: ${formatValue(
    mean
  ) + " " + yAxisName}`;
  const statsHtml = L.energy
    ? `${baseStats} , ${L.energy}: ${formatValue(energy)} ${yAxisName ? yAxisName+"h" :"kWh"})`
    : `${baseStats})`;
  return `<div style="display:flex;align-items:center;">${box(
    primaryColor
  )}<span>${statsHtml}</span></div>`;
}

export function createSimplifiedPaths({
  labels,
  data1,
  data2,
  innerWidth,
  x,
  y,
  name,
  showElectrical,
  showThermal,
  onlyThermal,
  chartType,
  rootEl,
}) {
  const linesG = d3.select(rootEl).select("g.series-lines");
  const needSimplify = labels.length > innerWidth * 1.2;
  const line = d3
    .line()
    .x((d, i) => x(labels[i]))
    .y((d) => y(d))
    .defined((d) => typeof d === "number" && !isNaN(d));
  const simplifiedData1 = progressiveSample(data1, labels, innerWidth);
  const simplifiedData2 = progressiveSample(data2, labels, innerWidth);
  const simplifiedLabels = needSimplify
    ? progressiveSample(labels, labels, innerWidth)
    : labels;
  const lineSimplified = d3
    .line()
    .x((d, i) => x(simplifiedLabels[i]))
    .y((d) => y(d))
    .defined((d) => typeof d === "number" && !isNaN(d));
  let path1 = null;
  let path2 = null;
  if (chartType === "line") {
    const dual = data1.length && data2.length;
    if (dual) {
      if (data1.length && showElectrical) {
        path1 = linesG
          .append("path")
          .datum(needSimplify ? simplifiedData1 : data1)
          .attr("fill", "none")
          .attr("stroke", "#42b4e6")
          .attr("stroke-width", 1.1)
          .attr("opacity", needSimplify ? 0.9 : 1)
          .attr("d", needSimplify ? lineSimplified : line);
      }
      if (data2.length && showThermal) {
        path2 = linesG
          .append("path")
          .datum(needSimplify ? simplifiedData2 : data2)
          .attr("fill", "none")
          .attr("stroke", "#3dcd58")
          .attr("stroke-width", 1.1)
          .attr("opacity", needSimplify ? 0.9 : 1)
          .attr("d", needSimplify ? lineSimplified : line);
      }
    } else if (onlyThermal && showThermal) {
      path1 = linesG
        .append("path")
        .datum(needSimplify ? simplifiedData2 : data2)
        .attr("fill", "none")
        .attr("stroke", "#3dcd58")
        .attr("stroke-width", 1.1)
        .attr("opacity", needSimplify ? 0.9 : 1)
        .attr("d", needSimplify ? lineSimplified : line);
    } else if (data1.length && showElectrical) {
      path1 = linesG
        .append("path")
        .datum(needSimplify ? simplifiedData1 : data1)
        .attr("fill", "none")
        .attr("stroke", "#42b4e6")
        .attr("stroke-width", 1.1)
        .attr("opacity", needSimplify ? 0.9 : 1)
        .attr("d", needSimplify ? lineSimplified : line);
    }
  }
  return { path1, path2, needSimplify, line, lineSimplified, simplifiedLabels };
}

export function attachRefine({
  needSimplify,
  path1,
  path2,
  data1,
  data2,
  line,
  name,
  showElectrical,
  showThermal,
  onlyThermal,
}) {
  if (!needSimplify) return;
  const refine = () => {
    const dual = data1.length && data2.length;
    if (dual) {
      if (path1 && showElectrical)
        path1.datum(data1).attr("opacity", 1).attr("d", line);
      if (path2 && showThermal)
        path2.datum(data2).attr("opacity", 1).attr("d", line);
    } else if (onlyThermal && path1 && showThermal && !data1.length) {
      path1.datum(data2).attr("opacity", 1).attr("d", line);
    } else if (path1 && data1.length && showElectrical) {
      path1.datum(data1).attr("opacity", 1).attr("d", line);
    }
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(refine, { timeout: 1000 });
  } else {
    setTimeout(refine, 120);
  }
}

export function createTooltip(rootEl, dark) {
  return d3
    .select(rootEl)
    .append("div")
    .style("position", "absolute")
    .style("pointer-events", "none")
    .style("background", dark ? "#222" : "#fff")
    .style("color", dark ? "#fff" : "#000")
    .style("padding", "6px 8px")
    .style("font-size", "12px")
    .style("border", "1px solid #666")
    .style("border-radius", "4px")
    .style("box-shadow", "0 2px 4px rgba(0,0,0,0.3)")
    .style("display", "none");
}

export function createCrosshair(g, height, dark) {
  return g
    .append("line")
    .attr("stroke", dark ? "#888" : "#555")
    .attr("y1", 0)
    .attr("y2", height)
    .attr("pointer-events", "none")
    .style("display", "none");
}

export function computeSignature({
  name,
  yAxisName,
  chartType,
  colorScheme,
  fullViewport,
  embedHeight,
  showMinimapProp,
  hideTypeToggle,
  isFullscreen,
  showElectrical,
  showThermal,
  data1,
  data2,
  labels,
  interval,
}) {
  const sampleHash = (arr) => {
    if (!arr || !arr.length) return "0";
    const stride = Math.max(1, Math.floor(arr.length / 512));
    let h = 0;
    for (let i = 0; i < arr.length; i += stride) {
      const v = arr[i];
      if (typeof v === "number" && !isNaN(v)) {
        h = (h * 31 + (Math.round(v * 1000) & 0xffff)) >>> 0;
      }
    }
    return h.toString(36);
  };
  const hash1 = sampleHash(data1);
  const hash2 = sampleHash(data2);
  return [
    name,
    yAxisName,
    chartType,
    colorScheme,
    fullViewport,
    embedHeight,
    showMinimapProp,
    hideTypeToggle,
    isFullscreen,
    showElectrical,
    showThermal,
    labels.length,
    data1.length,
    data2.length,
    hash1,
    hash2,
    interval,
  ].join("|");
}
